import { areDatesEqual } from '@zonofi/common';
import {
  AppliedDateRangeItem,
  AppliedFiltersData,
  AppliedListFilterItem,
  AppliedSectionFilterItem,
  AppliedTreeFilterItem,
  BooleanFilterKeys,
  FiltersData,
  ListAppliedItem,
  ListFilterKeys,
  SectionFilterKeys,
  SortDirection,
  TreeFilterKeys,
  TreeInputData,
} from '../model';

const getTreeSelected = (
  inputData: TreeInputData[],
  applyParent: boolean
): ListAppliedItem[] => {
  const selectedItem: ListAppliedItem[] = [];
  inputData.forEach(({ selected, data = [], id, value }) => {
    if (data.length) {
      const nonSelected = data.every(({ selected }) => !selected);
      if (applyParent && selected && nonSelected) {
        selectedItem.push({ id, value });
      }
      const selectedChildren = getTreeSelected(data, applyParent);
      selectedItem.push(...selectedChildren);
    } else if (selected) {
      selectedItem.push({ id, value });
    }
  });
  return selectedItem;
};

export const applySelectedFilters = (
  filters: FiltersData
): AppliedFiltersData => {
  const {
    booleanFilter = {},
    listFilter = {},
    sectionFilter = {},
    treeFilter = {},
    sortFilter = {},
    dateRange,
  } = filters;
  const appliedBoolean: BooleanFilterKeys[] = [];
  const appliedList: Partial<AppliedListFilterItem> = {};
  const appliedSection: Partial<AppliedSectionFilterItem> = {};
  const appliedTree: Partial<AppliedTreeFilterItem> = {};
  let appliedDateRange: AppliedDateRangeItem | undefined = undefined;

  const selectedSort = Object.values(sortFilter).find((x) => x.selected);

  for (const item in booleanFilter) {
    const filterKey = item as BooleanFilterKeys;
    if (booleanFilter[filterKey]) {
      appliedBoolean.push(filterKey);
    }
  }

  for (const item in listFilter) {
    const filterKey = item as ListFilterKeys;
    const listFilterItems = listFilter?.[filterKey]?.value || [];
    const selectedItems = listFilterItems.filter(
      (x) => x.selected && !x.isDefault
    );
    appliedList[filterKey] = selectedItems.map(({ id, value }) => ({
      id,
      value,
    }));
  }

  for (const item in sectionFilter) {
    const filterKey = item as SectionFilterKeys;
    const sectionFilterItems = (sectionFilter?.[filterKey]?.value || [])
      .map(({ title, data }) => data.map((x) => ({ ...x, title })))
      .flatMap((x) => x);
    const selectedItems = sectionFilterItems.filter((x) => x.selected);
    appliedSection[filterKey] = selectedItems.map(
      ({ id, value, title: section }) => ({
        id,
        value,
        section,
      })
    );
  }

  for (const item in treeFilter) {
    const filterKey = item as TreeFilterKeys;
    const treeFilterItem = treeFilter?.[filterKey]?.value || [];
    const applyParent = treeFilter?.[filterKey]?.applyParent ?? false;
    const selectedItems = getTreeSelected(treeFilterItem, applyParent);
    appliedTree[filterKey] = selectedItems;
  }

  if (dateRange) {
    const { startDate, endDate, defaultRange } = dateRange;
    const isDefault = defaultRange
      ? areDatesEqual(startDate, defaultRange.startDate) &&
        areDatesEqual(endDate, defaultRange.endDate)
      : false;
    appliedDateRange = { startDate, endDate, isDefault };
  }

  return {
    booleanFilter: appliedBoolean,
    listFilter: appliedList,
    sectionFilter: appliedSection,
    treeFilter: appliedTree,
    sortFilter: selectedSort
      ? {
          field: selectedSort.field,
          direction: selectedSort.selected as SortDirection,
        }
      : undefined,
    dateRange: appliedDateRange,
  };
};
